import { useTranslation } from 'react-i18next';
import { API_POST, DBRequest, DBUploadData, useConfig } from '../../api/api';
import {
  Box,
  Button,
  Collapse,
  Divider,
  LinearProgress,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { tokens } from '../../global/theme/tokens';
import SendIcon from '@mui/icons-material/Send';
import Loader from '../special/Loader';
import MediaPreview from '../exo/explorer/MediaPreview';
import MarqueeText from '../special/MarqueeText';
import FormFields from '../form/FormFields';
import * as Yup from 'yup';
import { useUserPreferences } from '../../global/useUserPreferences';
import useFetch from '../../api/useFetch';
import { useEmailClient } from '../email-system/useEmailClient';
import ExoDraftManager from '../exo/ExoDraftManager';
import { RenderHtmlEmail } from '../email-system/ECEmailConversation';
import ExoAlert from '../exo/ExoAlert';
import { hasPermissions } from '../../auth/hasPermissions';

function stripParticipantInteractions(participants) {
  if (!participants) return participants;
  return participants.map(({ participant, ...rest }) => rest);
}

export const NewEmail = ({
  selectedFolder,
  onNewMail,
  oldEmail,
  type,
  blockRendering,
  startData,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { emailCredentials } = useEmailClient();

  const { apiData } = useFetch(`text-blocks?type[eq]=signature`);

  const { t } = useTranslation();
  const config = useConfig();
  const { preferences, updatePreferences } = useUserPreferences();

  const [emailOptions, setEmailOptions] = useState(null);
  const [dataCollection, setDataCollection] = useState({});
  const [selectedCredentials, setSelectedCredentials] = useState(null);
  const [editMode, setEditMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCred, setSavedCred] = useState(null);

  useEffect(() => {
    if (!savedCred && emailCredentials) setSavedCred(emailCredentials);
  }, [emailCredentials]);

  useEffect(() => {
    if (savedCred) calculateOptions();
  }, [selectedFolder, savedCred]);

  function getLastCred() {
    var selectedCred = null;

    if (selectedFolder && !selectedFolder?.type) {
      savedCred?.data.forEach((cred) => {
        cred.inbox.folders.forEach((folder) => {
          if (folder.id === selectedFolder.id) {
            return (selectedCred = cred);
          }
        });
      });
    } else {
      selectedCred = savedCred?.data[0];
    }
    return selectedCred;
  }

  // actions
  function dataForBlank(email) {
    const selectedCred = getLastCred();
    setSelectedCredentials(selectedCred);

    return {
      credentialsId: selectedCred?.id,
      credentials: selectedCred,
      signature: findSignatureForCredentials(selectedCred),
      to: startData?.to,
    };
  }

  function dataForReply(email) {
    setEditMode(false);
    const cred = findCredentialsViaEmail(email) || getLastCred();
    setSelectedCredentials(cred);
    return {
      to: [email.from[0]],
      subject: `${t('RE')}: ${email.subject}`,
      credentialsId: cred?.id,
      credentials: cred,
      inReplyTo: oldEmail.id,
      additionalContent:
        email.htmlBody || email.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      signature: cred ? findSignatureForCredentials(cred) : null,
      inlineAttachments: email.inlineAttachments,
      project: oldEmail.project || null,
    };
  }
  function dataForReplyAll(email) {
    setEditMode(false);

    const cred = findCredentialsViaEmail(email) || getLastCred();
    if (cred) setSelectedCredentials(cred);
    return {
      to: [email.from[0]],
      cc: email.cc,
      subject: `${t('RE')}: ${email.subject}`,
      credentialsId: cred.id,
      credentials: cred,
      inReplyTo: oldEmail.id,
      additionalContent:
        email.htmlBody || email.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      signature: cred ? findSignatureForCredentials(cred) : null,
      inlineAttachments: email.inlineAttachments,
      project: oldEmail.project || null,
    };
  }
  function dataForForwarding(email) {
    const cred = findCredentialsViaEmail(email) || getLastCred();
    if (cred) setSelectedCredentials(cred);
    return {
      subject: `${t('FWD')}: ${email.subject}`,
      credentialsId: cred.id,
      credentials: cred,
      attachments: email.attachments,
      inlineAttachments: email.inlineAttachments,
      inReplyTo: oldEmail.id,
      additionalContent:
        email.htmlBody || email.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>'),
      signature: cred ? findSignatureForCredentials(cred) : null,
      project: oldEmail.project || null,
    };
  }

  function findCredentialsViaEmail(email) {
    const cred = savedCred?.data.find((obj) => {
      const matchesTo = email.to?.some((e) => e.email === obj.imapUsername);
      const matchesFrom = email.from?.some((e) => e.email === obj.imapUsername);
      return matchesTo || matchesFrom;
    });

    return cred || null;
  }

  function findSignatureForCredentials(cred) {
    if (!cred) return null;
    const signatureMap = preferences?.emailSignatureMap || {};
    const signatureId = signatureMap[cred.imapUsername];

    if (signatureId) {
      return apiData?.data.find((obj) => obj.id === signatureId) || null;
    }

    return apiData?.data?.[0] || null;
  }

  useEffect(() => {
    if (!preferences || !apiData?.data?.[0] || !selectedCredentials) return;
    const username = selectedCredentials.imapUsername;
    if (!username) return;
    const signatureMap = preferences.emailSignatureMap || {};
    const mappedId = signatureMap[username];
    const mappedExists =
      mappedId && apiData.data.some((s) => s.id === mappedId);
    if (mappedExists) return;
    updatePreferences(
      '',
      {
        emailSignatureMap: {
          ...signatureMap,
          [username]: apiData.data[0].id,
        },
      },
      true,
    );
  }, [selectedCredentials, apiData, preferences]);

  const typeMap = {
    blank: dataForBlank,
    reply: dataForReply,
    replyAll: dataForReplyAll,
    forwarding: dataForForwarding,
  };

  useEffect(() => {
    if (preferences && apiData && savedCred)
      setDataCollection(typeMap[type || 'blank'](oldEmail));
    // intentionally omit `preferences` — it changes after each updatePreferences
    // PATCH and would clobber the user's in-progress draft. Signature persistence
    // is handled by the dedicated effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldEmail, apiData, savedCred]);

  // form
  function calculateOptions() {
    var newEmailOptions = [];
    if (emailOptions) return;

    savedCred?.data.forEach((cred) => {
      const { color, id, imapUsername, label, smtpStatus } = cred;
      newEmailOptions.push({
        label: (
          <Box className="flex items-center">
            <Box sx={{ bgcolor: color }} />
            <Typography fontWeight={600}>{label}</Typography>
            <span className="opacity-50 pl-3">{`<${imapUsername}>`}</span>
            {smtpStatus !== 1 && (
              <Tooltip
                title={t(
                  'No permission to send mail from this address',
                )}
              >
                <WarningIcon
                  color="warning"
                  sx={{ fontSize: 16, ml: 1 }}
                />
              </Tooltip>
            )}
          </Box>
        ),
        value: id,
      });
    });
    setEmailOptions(newEmailOptions);
  }

  function sendEmail() {
    const fontUrl = `${config.apiUrl}/fonts/`;
    const defaultStyles = `
    <style>
        @font-face {
        font-family: 'Montserrat';
        src: url('${fontUrl}Montserrat.ttf') format('truetype');
        font-weight: 100 900;
        font-stretch: 75% 100%;
        font-style: normal;
      }
      @font-face {
        font-family: 'Montserrat';
        src: url('${fontUrl}Montserrat-Italic.ttf')
          format('truetype');
        font-weight: 100 900;
        font-stretch: 75% 100%;
        font-style: italic;
      }
      *{
        font-family: 'Montserrat';
        margin: 0;
      }
    </style>
    `;
    DBRequest({
      config,
      path: `imap-emails`,
      method: API_POST({
        ...dataCollection,
        to: stripParticipantInteractions(dataCollection.to),
        cc: stripParticipantInteractions(dataCollection.cc),
        bcc: stripParticipantInteractions(dataCollection.bcc),
        htmlBody: `<body style="background-color:rgb(255,255,255); padding:10px;">${defaultStyles} ${dataCollection.htmlBody}</body>`,
        attachments: [
          ...(dataCollection.attachments || []),
          ...(dataCollection.inlineAttachments || []),
        ],
      }),
      onLoading: setIsLoading,
      onResponse: onNewMail,
      onError: (error) =>
        setAlert({ open: true, type: 'error', message: error.message }),
    });
  }

  const fields = [
    {
      label: t('from'),
      key: 'credentialsId',
      options: emailOptions,
      type: 'customSelect',
    },
    {
      label: t('to'),
      key: 'to',
      type: 'participantsAutoComplete',
    },
    {
      label: t('cc'),
      key: 'cc',
      type: 'participantsAutoComplete',
    },
    {
      label: t('bcc'),
      key: 'bcc',
      type: 'participantsAutoComplete',
      allowSmartSelect: true,
    },
    ...(hasPermissions('project', 'read')
      ? [
          {
            label: t('Project'),
            key: 'project',
            type: 'project',
          },
        ]
      : []),
    {
      label: t('Attachments'),
      key: 'attachments',
      maxSizeKB: 25600,
      type: 'attachments',
      maxItems: 10,
      mediaType: 'PRIVATE',
    },
  ];

  // validation
  const emailSchema = Yup.object().shape({
    email: Yup.string().required('Email is required'),
  });

  const validationSchemaTop = Yup.object().shape({
    credentialsId: Yup.string().required(t('Is required')),

    to: Yup.array()
      .of(emailSchema)
      .when('bcc', (bcc, schema) => {
        const hasValidBcc =
          bcc &&
          bcc[0] &&
          bcc[0].some(
            (item) =>
              item?.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item.email),
          );
        return hasValidBcc ? schema : schema.min(1).required();
      }),
  });
  const validationSchemaBottom = Yup.object().shape({
    subject: Yup.string().required(t('Is required')),
    htmlBody: Yup.string().required(t('Is required')),
  });

  const [topIsValid, setTopIsValid] = useState(false);
  const [bottomIsValid, setBottomIsValid] = useState(false);

  const [defaultData, setDefaultData] = useState({});

  const [alert, setAlert] = useState({ type: 'error' });

  // Drag & Drop Upload
  const [uploadProgress, setUploadProgress] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles.length === 0) return;

      // Pre-Upload Validierung
      const existingCount = (dataCollection.attachments || []).length;
      const maxItems = 10;
      const maxSizeKB = 25600;

      // Max-Items Check
      if (existingCount + acceptedFiles.length > maxItems) {
        setAlert({
          open: true,
          type: 'warning',
          message: t('Maximum {{max}} attachments allowed', { max: maxItems }),
        });
        return;
      }

      // Max-Size Check (bytes -> KB)
      const existingSizeKB =
        (dataCollection.attachments || []).reduce(
          (sum, att) => sum + (att.fileSize || 0),
          0,
        ) / 1024;
      const newFileSizeKB =
        acceptedFiles.reduce((sum, file) => sum + file.size, 0) / 1024;

      if (existingSizeKB + newFileSizeKB > maxSizeKB) {
        setAlert({
          open: true,
          type: 'warning',
          message: t('Files exceed maximum size of {{max}}MB', {
            max: maxSizeKB / 1024,
          }),
        });
        return;
      }

      const formData = new FormData();
      formData.append('fileType', 'PRIVATE');
      acceptedFiles.forEach((file) => {
        formData.append('files[]', file);
      });

      setIsUploading(true);
      DBUploadData({
        config,
        path: 'files',
        formData,
        onProgressChange: setUploadProgress,
        onFinished: () => setIsUploading(false),
        onResponse: (response) => {
          const uploadedFiles = response.data.data;
          const existingAttachments = dataCollection.attachments || [];
          setDataCollection({
            ...dataCollection,
            attachments: [...existingAttachments, ...uploadedFiles],
          });
          setUploadProgress(null);
        },
        onError: (error) => {
          setAlert({
            open: true,
            type: 'error',
            message: error.message || t('Upload failed'),
          });
          setUploadProgress(null);
        },
      });
    },
    [config, dataCollection, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileDrop,
    noClick: true,
    multiple: true,
  });

  return (
    <Box
      {...getRootProps()}
      className="flex flex-col lg:flex-row w-full gap-4 lg:h-full relative"
      onKeyDown={(e) => e.stopPropagation()}
    >
      {/* Drag-Active Overlay */}
      {isDragActive && (
        <Box
          className="absolute inset-0 flex justify-center items-center backdrop-blur-lg z-50 rounded-lg"
          sx={{ backgroundColor: colors.info + '30' }}
        >
          <Typography variant="h2" sx={{ color: colors.info }}>
            {t('Drop files to attach')}
          </Typography>
        </Box>
      )}

      {/* Upload Progress Overlay */}
      {isUploading && (
        <Box
          className="absolute inset-0 flex flex-col justify-center items-center backdrop-blur-lg z-50 rounded-lg gap-2"
          sx={{ backgroundColor: colors.info + '30' }}
        >
          <Typography variant="h4" sx={{ color: colors.info }}>
            {t('Uploading...')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress || 0}
            sx={{
              width: '200px',
              '& .MuiLinearProgress-bar': { backgroundColor: colors.info },
            }}
          />
          <Typography sx={{ color: colors.info }}>
            {uploadProgress || 0}%
          </Typography>
        </Box>
      )}

      {/* Hidden file input for Dropzone */}
      <input {...getInputProps()} />

      <ExoDraftManager
        dataCollection={dataCollection}
        draftType={'email'}
        startDataCollection={startData}
        setDataCollection={(e) => {
          setDataCollection(e);
          setDefaultData(e);
        }}
        titleFunction={(e) => e.subject}
        className=" z-10"
        onChange={(e) => setDataCollection({ ...dataCollection, ...e })}
      />
      <Box className="flex flex-col gap-4 h-full overflow-y-auto w-full max-w-[900px]">
        <Loader active={isLoading} />
        <Box className="grid grid-cols-2 w-full gap-4 items-start md:flex">
          <Button
            variant="contained"
            color="success"
            disableElevation
            onClick={sendEmail}
            sx={{ height: '46px' }}
            className=" col-span-2"
            disabled={!topIsValid || !bottomIsValid}
          >
            <SendIcon />
          </Button>
          <Box className="w-full col-span-2">
            <Collapse in={editMode}>
              <Box className="flex flex-col gap-4 pt-3 pb-2">
                <FormFields
                  dataCollection={dataCollection}
                  updateDataCollection={(data) => {
                    var cred = {};
                    if (data.credentialsId) {
                      cred = savedCred?.data.find(
                        (obj) => obj.id === data.credentialsId,
                      );
                    }
                    setDataCollection({ ...dataCollection, ...data, ...cred });
                  }}
                  size="small"
                  fields={fields}
                  validationSchema={validationSchemaTop}
                  onValidationChange={setTopIsValid}
                />
                <Divider />
              </Box>
            </Collapse>

            <Collapse in={!editMode}>
              <Button
                onClick={() => setEditMode(true)}
                sx={{
                  bgcolor: colors.card,
                  display: 'block',
                  textAlign: 'left',
                  border: `2px solid ${
                    topIsValid ? 'transparent' : colors.warning
                  }`,
                  ':hover': {
                    bgcolor: colors.bgInput,
                  },
                }}
                className="w-full rounded-lg p-3"
              >
                <Typography fontSize={12}>
                  <span style={{ fontWeight: 500 }}>{t('from')}:</span>
                  <span className="opacity-50 pl-2">
                    {selectedCredentials?.imapUsername}
                  </span>
                </Typography>
                <Divider />
                <Typography fontSize={12} className="pt-2">
                  <span style={{ fontWeight: 500 }}>{t('to')}:</span>
                  {dataCollection?.to?.map((to, index) => (
                    <span className="opacity-50 pl-2">
                      {to.email}
                      {dataCollection?.to.length !== index + 1 ? ',' : ''}
                    </span>
                  ))}
                </Typography>
                {dataCollection?.cc?.length > 0 && (
                  <Typography fontSize={12}>
                    <span style={{ fontWeight: 500 }}>{t('cc')}:</span>
                    {dataCollection?.cc?.map((cc, index) => (
                      <span className="opacity-50 pl-2">
                        {cc.email}
                        {dataCollection?.cc.length !== index + 1 ? ',' : ''}
                      </span>
                    ))}
                  </Typography>
                )}
                {dataCollection?.attachments && (
                  <Box className="flex items-start w-full gap-2">
                    <Typography className="pr-2">
                      {t('Attachments')}:
                    </Typography>
                    <Box className="flex items-start w-full gap-2 flex-wrap">
                      {dataCollection?.attachments.map((att) => (
                        <Box className="flex gap-1 items-center">
                          <MediaPreview
                            src={att.thumbnailUrl || att.temporaryUrl}
                            size="30px"
                            mimeType={att.mimeType}
                            allowPreview
                          />
                          <MarqueeText text={att.title} width="200px" />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Button>
            </Collapse>
          </Box>
        </Box>
        <Box
          className="flex flex-col gap-2 w-full h-full"
          onClick={() => setEditMode(false)}
        >
          <FormFields
            dataCollection={dataCollection}
            startDataCollection={defaultData}
            updateDataCollection={(data) =>
              setDataCollection({ ...dataCollection, ...data })
            }
            onPreventEnter={() => {}}
            fields={[
              {
                label: t('Subject'),
                key: 'subject',
                type: 'text',
                textBlockType: 'emailSubject',
              },
              {
                label: '',
                key: 'htmlBody',
                textBlockType: 'block',
                allowSignature: true,
                allowBookingLink: true,
                signatureKey: 'signature',
                type: 'richTextEditor',
                defaultFontSize: '12px',
                afterKey: 'additionalContent',
              },
            ]}
            validationSchema={validationSchemaBottom}
            onValidationChange={setBottomIsValid}
          />
        </Box>
      </Box>
      {oldEmail && (
        <Box
          className="h-full lg:w-[60vw] rounded-lg p-2 overflow-y-auto"
          sx={{
            bgcolor: 'rgba(235, 235, 235, 0.8)',
          }}
        >
          <Typography
            fontWeight={500}
            className="opacity-50"
            sx={{ color: 'rgb(30,30,30)' }}
          >
            {t('Previous messages')}
          </Typography>
          <RenderHtmlEmail
            htmlBody={
              oldEmail.htmlBody ||
              oldEmail.textBody.replace(/(?:\r\n|\r|\n)/g, '<br>')
            }
            inlineAttachments={oldEmail?.inlineAttachments}
          />
        </Box>
      )}
      <ExoAlert
        alert={alert}
        open={alert.open}
        onClose={() => setAlert({ ...alert, open: false })}
      />
    </Box>
  );
};
